import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSiteSettings } from "../contexts/SiteSettingsContext";

// Builds the `meta` prop UserLayout expects for single-instance pages
// (category, sub-category, brand, blog, product) whose SEO fields come
// from the fetched entity itself rather than the generic page_meta table.
// Admin-entered meta_title/meta_description/meta_keywords win; otherwise
// fall back to sensible derived values passed in by the caller.
export const buildEntityMeta = (entity, {
  fallbackTitle,
  fallbackDescription,
  fallbackKeywords,
  ogType = "website",
  ogImage,
} = {}) => {
  if (!entity) return null;

  const title = entity.meta_title || fallbackTitle;
  const description = entity.meta_description || fallbackDescription;
  const keywords = entity.meta_keywords || fallbackKeywords || null;
  const canonicalHref = typeof window !== "undefined" ? window.location.href : "";

  return {
    title,
    description,
    keywords,
    og_tags: {
      "og:type": ogType,
      "og:title": title,
      "og:description": description,
      ...(ogImage ? { "og:image": ogImage } : {}),
      "og:url": canonicalHref,
    },
    canonical_links: canonicalHref
      ? [{ tag: "link", attributes: [{ key: "rel", value: "canonical" }, { key: "href", value: canonicalHref }] }]
      : [],
  };
};

const useFrontendMeta = (overridePath) => {
  const { pathname } = useLocation();
  const path = overridePath ?? pathname;
  const { website = {}, contact = null, page_meta = [], social_links = [] } = useSiteSettings();

  return useMemo(() => {
    const firstSegment = path.replace(/^\//, "").split("/")[0] || "home";
    const pageNameByRoute = {
       categories: "categories",
       category: "category", 
      "business-profile": "business-profile",
       
    };

    const mappedPageKey = pageNameByRoute[firstSegment] ?? firstSegment;

    const meta =
      page_meta.find((m) => m.page_name === mappedPageKey && m.status && !m.deleted_at) ||
      page_meta.find((m) => m.page_name === "default" && m.status && !m.deleted_at);

    const companyName = website.website_name || "Ukasha Enterprise";
    return {
      companyName,
      whatsappNumber: process.env.REACT_APP_WHATSAPP_NUMBER || null,
      title: meta?.meta_title || companyName,
      description: meta?.meta_description || companyName,
      keywords: meta?.meta_keywords || null,
      og_tags: meta?.og_tags || {},
      canonical_links: meta?.canonical_links || [],
      footer: {
        description: website.footer_about || null,
        logo: website.header_logo || null,
        socialLinks: social_links || [],
        contact: contact || null,
      },
      home_about_image: website.home_about_image || null,
    };
  }, [path, website, contact, page_meta, social_links]);
};

export default useFrontendMeta;
